import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authService } from '../services/auth';
import { apiClient, api } from '../services/api';

const schema = z.object({
  email: z.string().email('Email invalide'),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPassword() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/config')
      .then((res) => { if (res.data?.logoUrl) setLogoUrl(res.data.logoUrl); })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const message = await authService.forgotPassword(data.email);
      setSuccess(message);
    } catch (err) {
      setError(apiClient.getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, hsl(30 36% 89%) 0%, hsl(30 36% 95%) 100%)' }}>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Univers Massages"
              className="mx-auto h-36 w-auto mb-6 rounded-2xl"
            />
          )}
          <p className="text-sm" style={{ color: 'hsl(17 47% 58%)' }}>
            Réinitialisation du mot de passe
          </p>
        </div>

        <div className="mt-6 sm:mt-8 bg-white/80 backdrop-blur-sm px-4 sm:px-8 py-6 sm:py-10 shadow-sm rounded-lg" style={{ borderTop: '3px solid hsl(17 47% 68%)' }}>
          {success ? (
            <div className="space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-800">{success}</p>
              </div>
              <p className="text-sm text-gray-600 text-center">
                Vérifiez votre boîte mail (et vos spams).
              </p>
              <Link
                to="/login"
                className="block text-center text-sm font-medium hover:underline"
                style={{ color: 'hsl(17 47% 48%)' }}
              >
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <p className="text-sm text-gray-600">
                Entrez votre adresse email. Si un compte existe, vous recevrez un lien pour réinitialiser votre mot de passe.
              </p>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  {...register('email')}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full text-white"
                style={{ background: 'linear-gradient(135deg, hsl(17 47% 68%) 0%, hsl(17 47% 58%) 100%)' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi...' : 'Envoyer le lien'}
              </Button>

              <Link
                to="/login"
                className="block text-center text-sm font-medium hover:underline"
                style={{ color: 'hsl(17 47% 48%)' }}
              >
                Retour à la connexion
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
